(function () {
  const ROOT_SELECTOR = '.elorene-cfy[data-section-id]';
  const STORAGE_KEY = 'viewedProducts';
  const MAX_SEEDS = 3;

  function getViewedProductIds() {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(value)) return [];

      return [...new Set(value.map(String).filter(Boolean))].slice(0, MAX_SEEDS);
    } catch (_) {
      return [];
    }
  }

  function productIdFromSlide(slide) {
    return slide.querySelector('[data-product-id]')?.getAttribute('data-product-id') || '';
  }

  function uniqueSlides(slides, seen, excludedIds, maxProducts) {
    const output = [];

    for (const sourceSlide of slides) {
      const productId = productIdFromSlide(sourceSlide);
      if (!productId || seen.has(productId) || excludedIds.has(productId)) continue;

      seen.add(productId);
      output.push(sourceSlide.cloneNode(true));
      if (seen.size >= maxProducts) break;
    }

    return output;
  }

  function resetSlides(slideshow, slides) {
    const scroller = slideshow.querySelector('slideshow-slides');
    if (!scroller || !slides.length) return false;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      slide.setAttribute('ref', 'slides[]');
      slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
    });

    scroller.replaceChildren(...slides);
    slideshow.setAttribute('initial-slide', '0');
    slideshow.setAttribute('data-slide-step', '1');
    slideshow.setAttribute('data-elorene-drag-scroll', 'true');
    slideshow.removeAttribute('disabled');

    const timeline = slides.map((_, index) => `--slide-${index}`).join(', ');
    slideshow.style.setProperty('--slideshow-timeline', timeline);
    return true;
  }

  async function fetchRecommendationSection(root, productId) {
    const sectionId = root.dataset.sectionId;
    const routeRoot = window.Shopify?.routes?.root || '/';
    const endpoint = new URL(`${routeRoot}recommendations/products`, window.location.origin);

    endpoint.searchParams.set('product_id', productId);
    endpoint.searchParams.set('section_id', sectionId);
    endpoint.searchParams.set('limit', '10');
    endpoint.searchParams.set('intent', 'related');

    const response = await fetch(endpoint.toString(), {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) return null;

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const responseRoot = documentFragment.querySelector(ROOT_SELECTOR);

    if (!responseRoot || responseRoot.dataset.source !== 'recommendations') return null;
    return responseRoot.querySelector('slideshow-component');
  }

  async function hydrate(root) {
    if (root.dataset.hydrated === 'true' || root.dataset.loading === 'true') return;

    const viewedIds = getViewedProductIds();
    const currentSlideshow = root.querySelector('slideshow-component');
    if (!currentSlideshow) return;

    root.dataset.hydrated = 'true';
    if (!viewedIds.length || window.Shopify?.designMode) return;

    root.dataset.loading = 'true';

    const maxProducts = Math.max(20, Math.min(25, Number(root.dataset.maxProducts) || 24));
    const fallbackSlides = [...currentSlideshow.querySelectorAll('slideshow-slides > slideshow-slide')];
    const excludedIds = new Set(viewedIds);
    const seen = new Set();

    try {
      const results = await Promise.allSettled(viewedIds.map((id) => fetchRecommendationSection(root, id)));
      const recommendationCarousels = results
        .filter((result) => result.status === 'fulfilled' && result.value)
        .map((result) => result.value);

      if (!recommendationCarousels.length) return;

      const finalSlides = [];
      for (const carousel of recommendationCarousels) {
        const slides = [...carousel.querySelectorAll('slideshow-slides > slideshow-slide')];
        finalSlides.push(...uniqueSlides(slides, seen, excludedIds, maxProducts));
        if (finalSlides.length >= maxProducts) break;
      }

      if (finalSlides.length < maxProducts) {
        const remaining = maxProducts - finalSlides.length;
        finalSlides.push(...uniqueSlides(fallbackSlides, seen, excludedIds, seen.size + remaining));
      }

      if (!finalSlides.length) return;

      const replacement = recommendationCarousels[0].cloneNode(true);
      if (!resetSlides(replacement, finalSlides.slice(0, maxProducts))) return;

      currentSlideshow.replaceWith(replacement);
      root.dataset.source = 'personalized';
    } catch (error) {
      console.warn('Chosen For You recommendations could not be loaded.', error);
    } finally {
      root.dataset.loading = 'false';
    }
  }

  function init(scope) {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    roots.forEach(hydrate);
  }

  function boot() {
    init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
